import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { EmptyState } from '../components/ui/EmptyState';
import { SearchInput } from '../components/ui/SearchInput';
import { Switch } from '../components/ui/Switch';
import { api } from '../lib/api';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { Loader } from '../components/ui/Spinner';

type Region = { id: string; name: string; code: string; isActive: boolean };

function RegionForm({ initial, onSubmit, onCancel, pending }: { initial?: Region; onSubmit: (v: { name: string; code: string; isActive: boolean }) => void; onCancel: () => void; pending: boolean }) {
  const [name, setName] = useState(initial?.name ?? '');
  const [code, setCode] = useState(initial?.code ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  return (
    <form className="card mb-6 grid gap-4 sm:grid-cols-2" onSubmit={(e: FormEvent) => { e.preventDefault(); onSubmit({ name, code: code.toUpperCase(), isActive }); }}>
      <label className="label">Region name<input className="field mt-1" required placeholder="e.g. Rajkot" value={name} onChange={(e) => setName(e.target.value)} /></label>
      <label className="label">Short code<input className="field mt-1" required placeholder="e.g. RJK" maxLength={20} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} /></label>
      {initial && <label className="label flex items-center gap-3">Active<Switch checked={isActive} onChange={setIsActive} /></label>}
      <div className="flex items-end gap-2 sm:col-span-2">
        <button className="btn-primary" disabled={pending}>Save</button>
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export function RegionsPage() {
  const client = useQueryClient();
  const { success, error: toastError } = useToast();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Region | null>(null);
  const [q, setQ] = useState('');
  const regions = useQuery({ queryKey: ['regions'], queryFn: async () => (await api.get<Region[]>('/api/regions')).data });

  const create = useMutation({
    mutationFn: (body: { name: string; code: string }) => api.post('/api/regions', body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['regions'] }); success('Region created'); setOpen(false); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not create region')),
  });
  const update = useMutation({
    mutationFn: ({ id, ...body }: { id: string; name: string; code: string; isActive: boolean }) => api.patch(`/api/regions/${id}`, body),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['regions'] }); success('Region updated'); setEditing(null); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not update region')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/regions/${id}`),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['regions'] }); success('Region deleted'); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not delete — branches or region heads may still reference it')),
  });

  const needle = q.trim().toLowerCase();
  const filtered = (regions.data ?? []).filter((r) => !needle || r.name.toLowerCase().includes(needle) || r.code.toLowerCase().includes(needle));

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-3">
        <div><h1 className="text-3xl font-bold">Regions</h1><p className="mt-1 text-muted">Group branches by city or area for region heads.</p></div>
        <button className="btn-primary" onClick={() => { setOpen(!open); setEditing(null); }}><Plus size={18} /> New</button>
      </div>

      {open && <RegionForm onSubmit={(v) => create.mutate(v)} onCancel={() => setOpen(false)} pending={create.isPending} />}
      {editing && <RegionForm initial={editing} onSubmit={(v) => update.mutate({ id: editing.id, ...v })} onCancel={() => setEditing(null)} pending={update.isPending} />}

      {!regions.isLoading && (regions.data?.length ?? 0) > 0 && (
        <SearchInput className="mb-4 max-w-sm" placeholder="Search by name or code…" value={q} onChange={(e) => setQ(e.target.value)} />
      )}

      {regions.isLoading && <Loader />}
      {!regions.isLoading && regions.data?.length === 0 && <EmptyState title="No regions yet" hint="Add one to start grouping branches." />}
      {!regions.isLoading && (regions.data?.length ?? 0) > 0 && filtered.length === 0 && <EmptyState title="No regions match your search" />}

      {filtered.length > 0 && (
        <div className="card overflow-x-auto !p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Code</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((region) => (
                <tr key={region.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-3"><strong className={region.isActive ? '' : 'text-muted line-through'}>{region.name}</strong></td>
                  <td className="px-4 py-3 text-muted">{region.code}</td>
                  <td className="px-4 py-3">{region.isActive ? <span className="badge bg-green-50 text-green-700">Active</span> : <span className="badge bg-red-50 text-red-700">Inactive</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button className="btn-secondary !px-3" aria-label="Edit" onClick={() => { setEditing(region); setOpen(false); }}><Pencil size={16} /></button>
                      <button className="btn-secondary !px-3" aria-label="Delete" onClick={() => { if (confirm(`Delete "${region.name}"?`)) remove.mutate(region.id); }}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
